import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/authService';
import { refreshAgentToken } from '@/utils/auth';
import { getWhitelabelAgentId } from '@/utils/branding';

const SESSION_EXPIRY_BUFFER_MS = 60 * 1000; // Log out 60s before actual expiry for UX

/**
 * Hook that monitors token expiry and triggers auto-logout when the session expires.
 * - Sets a timeout based on the stored `tokenExpAgent` value
 * - Re-checks on tab visibility change (handles laptop sleep/wake)
 * - Gracefully skips if no expiry is stored (old sessions without exp)
 *
 * Should be used in a top-level component inside BrowserRouter (e.g., AppRouter).
 */
export const useSessionTimeout = () => {
    const navigate = useNavigate();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const performLogout = useCallback(() => {
        console.warn('Session expired — auto-logout triggered');
        const whitelabelAgentId = getWhitelabelAgentId();
        authService.logout();
        navigate(whitelabelAgentId ? `/agent-login?agentId=${whitelabelAgentId}` : '/agent-login', { replace: true });
    }, [navigate]);

    // Proactively refresh before expiry; log out only if refresh fails.
    const proactiveRefresh = useCallback(async () => {
        const ok = await refreshAgentToken();
        if (ok) {
            setupTimerRef.current?.(); // reschedule against the new expiry
        } else {
            performLogout();
        }
    }, [performLogout]);

    // Ref indirection so proactiveRefresh can reschedule without a dep cycle
    const setupTimerRef = useRef<(() => void) | null>(null);

    const setupTimer = useCallback(() => {
        clearTimer();

        if (!authService.isAuthenticated()) return;

        const tokenExpStr = localStorage.getItem('tokenExpAgent');
        if (!tokenExpStr) return;

        const expSeconds = parseInt(tokenExpStr, 10);
        if (isNaN(expSeconds)) return;

        const nowSeconds = Math.floor(Date.now() / 1000);
        const remainingMs = (expSeconds - nowSeconds) * 1000;

        if (remainingMs <= 0) {
            // Already expired — attempt refresh, log out if it fails
            proactiveRefresh();
            return;
        }

        // Fire refresh slightly before actual expiry so the user doesn't
        // experience a failed API call right at the boundary
        const timeoutMs = Math.max(remainingMs - SESSION_EXPIRY_BUFFER_MS, 0);

        timerRef.current = setTimeout(() => {
            proactiveRefresh();
        }, timeoutMs);
    }, [proactiveRefresh, clearTimer]);

    setupTimerRef.current = setupTimer;

    useEffect(() => {
        setupTimer();

        // Re-check when user returns to the tab (handles sleep/wake, long background)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                setupTimer();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearTimer();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [setupTimer, clearTimer]);
};
